<template>
  <div :class="`form-group ${error ? 'has-error' : ''}`">
    <label v-show="label" class="control-label">
      {{ label }}
      <span v-if="$attrs.required || requiredMark" class="text-danger">*</span>
    </label>
    <div
      class="c-input-wrapper"
      :class="{ 'has-icon': !!icon, 'has-eye': canTogglePassword, 'has-icon-and-eye': !!icon && canTogglePassword }"
    >
      <input
          :class="`form-control input-${size}`"
          :type="inputType"
          :id="id"
          :name="name"
          :value="value"
          v-bind="$attrs"
          @blur="$emit('blur', $event.target.value)"
          @input="$emit('input', $event.target.value)"
          @change="$emit('change', $event.target.value)"
      />
      <button
        v-if="canTogglePassword"
        type="button"
        class="c-input-eye"
        :aria-label="showPassword ? 'Hide password' : 'Show password'"
        @click="showPassword = !showPassword"
      >
        <svg v-show="!showPassword" class="svg-inline--fa fa-eye-slash" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="eye-slash" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" data-fa-i2svg=""><path fill="currentColor" d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"></path></svg>
        <svg v-show="showPassword" class="svg-inline--fa fa-eye" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="eye" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" data-fa-i2svg=""><path fill="currentColor" d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"></path></svg>
      </button>

      <i
        v-if="icon"
        class="fa-regular c-input-icon"
        :class="[icon, { 'c-input-icon--with-eye': canTogglePassword }]"
      ></i>
    </div>

    <div v-if="showCounter" class="c-field-meta">
      <div class="c-field-meta__message">
        <small class="text-danger" v-if="error" v-html="error"></small>
        <span class="help-block" v-if="help" v-html="help"></span>
      </div>
      <small class="c-field-counter" :class="counterState && `c-field-counter--${counterState}`" aria-hidden="true">
        {{ currentLength }} / {{ maxLength }}
      </small>
      <span class="sr-only" aria-live="polite">{{ counterAnnouncement }}</span>
    </div>
    <template v-else>
      <small class="text-danger" v-if="error" v-html="error"></small>
      <span class="help-block" v-if="help" v-html="help"></span>
    </template>
  </div>
</template>

<script>
export default {
  props: [
    'value',
    'error',
    'label',
    'help',
    'id',
    'type',
    'name',
    'requiredMark',
    'icon',
    'size',
    'passwordToggle',
    // Shows "12 / 100" under the field; the limit comes from the `maxlength` attr.
    'counter'
  ],
  data() {
    return {
      showPassword: false
    }
  },
  computed: {
    isPassword() {
      return (this.type || 'text') === 'password';
    },

    // чи треба показувати тумблер (око) саме для цього інпута
    canTogglePassword() {
      return this.isPassword && !!this.passwordToggle;
    },

    // фактичний type, який піде в <input :type="...">
    // якщо тумблер увімкнений - перемикаємо між password/text
    // якщо ні - буде як раніше: type || 'text'
    inputType() {
      const t = this.type || 'text';

      if (t === 'password' && this.canTogglePassword) {
        return this.showPassword ? 'text' : 'password';
      }

      return t;
    },

    maxLength() {
      return Number(this.$attrs.maxlength) || 0;
    },

    showCounter() {
      return this.counter !== undefined && this.counter !== false && this.maxLength > 0;
    },

    currentLength() {
      return String(this.value ?? '').length;
    },

    counterState() {
      if (this.currentLength >= this.maxLength) return 'limit';
      if (this.currentLength >= this.maxLength * 0.9) return 'near';
      return '';
    },

    // Announced only close to the limit so screen readers aren't spammed on every keystroke.
    counterAnnouncement() {
      if (this.counterState === 'limit') return 'Character limit reached';
      if (this.counterState === 'near') return `${this.maxLength - this.currentLength} characters remaining`;
      return '';
    },
  },
  emits: ['update:modelValue']
}
</script>

<style scoped lang="scss">
.c-input-wrapper {
  position: relative;

  &.has-icon {
    input {
      padding-right: 30px; /* space for the icon only when it exists */
    }
  }

  &.has-eye {
    input {
      padding-right: 30px; /* місце під "око" */
    }
  }

  &.has-icon-and-eye {
    input {
      padding-right: 54px; /* місце під дві іконки справа */
    }
  }

  .c-input-icon {
    pointer-events: none;
    position: absolute;
    right: 10px;
    top: calc(50% - 8px);
  }

  /* якщо є "око", декоративну icon зсуваємо трохи лівіше */
  .c-input-icon--with-eye {
    right: 34px;
  }

  /* кнопка "око" */
  .c-input-eye {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);

    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;

    cursor: pointer;
    line-height: 1;

    /* щоб було приємніше клікати */
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:focus {
      outline: none;
    }

    &:hover {
      opacity: 0.8;
    }

    /* якщо потрібно буде трохи видимий фокус (опціонально):
    &:focus-visible {
      outline: 2px solid rgba(0, 0, 0, 0.2);
      outline-offset: 2px;
      border-radius: 3px;
    }
    */
  }
}

.c-field-meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  &__message {
    min-width: 0;
  }
}

.c-field-counter {
  flex-shrink: 0;
  margin-left: auto;
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.4;
  color: #8a94a0;
  // Fixed-width digits keep the counter from jittering while typing.
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  &--near {
    color: #b26a00;
  }

  &--limit {
    color: #a94442;
    font-weight: 600;
  }
}
</style>

